import React, { useEffect, useState } from 'react';
import { axiosKK } from '../../../services/networkRequest';
import errorMessage from '../../../services/errorMessage';
import { OrderIssueType, StageProgress } from '../../../models/APIModels';
import {
  FilterBar,
  DataTable,
  DataTableColumn,
  ExportCsvButton,
  EmptyState,
  AdminNotice,
} from '../../../components/admin/Shared';
import { DateDropdown } from '../../../components/DateDropdown';

type SubTab = 'inbound' | 'outbound' | 'storage' | 'box-history';

interface InboundOrderRow {
  orderId: number;
  customer?: string;
  courier?: string;
  date?: string;
  boxes?: number;
  status?: string;
  unloadProgress?: StageProgress;
  issues?: { type: OrderIssueType }[];
}
interface InboundResponse {
  orders?: InboundOrderRow[];
  rows?: InboundOrderRow[];
}

interface OutboundOrderRow {
  orderId: number;
  customer?: string;
  courier?: string;
  date?: string;
  boxes?: number;
  status?: string;
  pickProgress?: StageProgress;
  holdoutProgress?: StageProgress;
  onsProgress?: StageProgress;
  issues?: { type: OrderIssueType }[];
}
interface OutboundResponse {
  orders?: OutboundOrderRow[];
  rows?: OutboundOrderRow[];
}

interface StorageRow {
  orderId: number;
  customer?: string;
  boxes?: number;
  months?: number;
  amount?: number;
}
interface StorageResponse {
  rows?: StorageRow[];
  orders?: StorageRow[];
}

interface BoxHistoryRow {
  bid?: string;
  orderId?: number;
  customer?: string;
  location?: string;
  lastScan?: string;
}
interface BoxHistoryResponse {
  rows?: BoxHistoryRow[];
  boxes?: BoxHistoryRow[];
  items?: BoxHistoryRow[];
}

function monthISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function progressLabel(p?: StageProgress): string {
  if (!p) return '—';
  return `${p.scanned ?? 0}/${p.total ?? 0}`;
}

function issuesLabel(items?: { type: OrderIssueType }[]): string {
  if (!items || items.length === 0) return '—';
  return items.map((i) => i.type).join(', ');
}

const INBOUND_COLUMNS: DataTableColumn<InboundOrderRow>[] = [
  { key: 'orderId', label: 'Order', render: (r) => `#${r.orderId}` },
  { key: 'customer', label: 'Customer', render: (r) => r.customer || '—' },
  { key: 'courier', label: 'Courier', render: (r) => r.courier || '—' },
  { key: 'date', label: 'Date', render: (r) => r.date || '—' },
  { key: 'boxes', label: 'Boxes', render: (r) => r.boxes ?? '—' },
  { key: 'status', label: 'Status', render: (r) => r.status || '—' },
  { key: 'unloadProgress', label: 'Unload', render: (r) => progressLabel(r.unloadProgress) },
  { key: 'issues', label: 'Issues', render: (r) => issuesLabel(r.issues) },
];

const OUTBOUND_COLUMNS: DataTableColumn<OutboundOrderRow>[] = [
  { key: 'orderId', label: 'Order', render: (r) => `#${r.orderId}` },
  { key: 'customer', label: 'Customer', render: (r) => r.customer || '—' },
  { key: 'courier', label: 'Courier', render: (r) => r.courier || '—' },
  { key: 'date', label: 'Date', render: (r) => r.date || '—' },
  { key: 'boxes', label: 'Boxes', render: (r) => r.boxes ?? '—' },
  { key: 'status', label: 'Status', render: (r) => r.status || '—' },
  { key: 'pickProgress', label: 'Pick', render: (r) => progressLabel(r.pickProgress) },
  { key: 'holdoutProgress', label: 'Holdout', render: (r) => progressLabel(r.holdoutProgress) },
  { key: 'onsProgress', label: 'ONS', render: (r) => progressLabel(r.onsProgress) },
  { key: 'issues', label: 'Issues', render: (r) => issuesLabel(r.issues) },
];

const STORAGE_COLUMNS: DataTableColumn<StorageRow>[] = [
  { key: 'orderId', label: 'Order', render: (r) => `#${r.orderId}` },
  { key: 'customer', label: 'Customer', render: (r) => r.customer || '—' },
  { key: 'boxes', label: 'Boxes', render: (r) => r.boxes ?? '—' },
  { key: 'months', label: 'Months', render: (r) => r.months ?? '—' },
  {
    key: 'amount',
    label: 'Amount',
    render: (r) => (r.amount != null ? `£${(r.amount / 100).toFixed(2)}` : '—'),
  },
];

const BOX_HISTORY_COLUMNS: DataTableColumn<BoxHistoryRow>[] = [
  { key: 'bid', label: 'BID', render: (r) => r.bid || '—' },
  { key: 'orderId', label: 'Order', render: (r) => (r.orderId != null ? `#${r.orderId}` : '—') },
  { key: 'customer', label: 'Customer', render: (r) => r.customer || '—' },
  { key: 'location', label: 'Location', render: (r) => r.location || '—' },
  { key: 'lastScan', label: 'Last scan', render: (r) => r.lastScan || '—' },
];

interface WarehouseTabProps {
  partnerId: number | null;
}

export default function WarehouseTab({ partnerId }: WarehouseTabProps) {
  const [tab, setTab] = useState<SubTab>('inbound');

  const [date, setDate] = useState<string>(todayISO);
  const [month, setMonth] = useState<string>(monthISO);
  const [search, setSearch] = useState('');
  const [issueFilter, setIssueFilter] = useState<OrderIssueType | ''>('');

  const [inbound, setInbound] = useState<InboundOrderRow[]>([]);
  const [outbound, setOutbound] = useState<OutboundOrderRow[]>([]);
  const [storage, setStorage] = useState<StorageRow[]>([]);
  const [history_, setHistory] = useState<BoxHistoryRow[]>([]);

  const [loading, setLoading] = useState(false);
  const [netError, setNetError] = useState<string>();

  useEffect(() => {
    if (partnerId == null) {
      setInbound([]);
      setOutbound([]);
      setStorage([]);
      setHistory([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);

    if (tab === 'inbound') {
      const params: Record<string, unknown> = { date, partnerId };
      if (issueFilter) params.issue = issueFilter;
      axiosKK
        .get<InboundResponse>('/logistics/dashboard/warehouse/inbound', { params })
        .then(({ data }) => {
          if (cancelled) return;
          setInbound(data.orders || data.rows || []);
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else if (tab === 'outbound') {
      const params: Record<string, unknown> = { date, partnerId };
      if (issueFilter) params.issue = issueFilter;
      axiosKK
        .get<OutboundResponse>('/logistics/dashboard/warehouse/outbound', { params })
        .then(({ data }) => {
          if (cancelled) return;
          setOutbound(data.orders || data.rows || []);
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else if (tab === 'storage') {
      axiosKK
        .get<StorageResponse>('/logistics/dashboard/warehouse/storage', {
          params: { month, partnerId },
        })
        .then(({ data }) => {
          if (cancelled) return;
          setStorage(data.rows || data.orders || []);
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      const params: Record<string, unknown> = { month, partnerId };
      if (search) params.search = search;
      axiosKK
        .get<BoxHistoryResponse>('/logistics/dashboard/warehouse/box-history', { params })
        .then(({ data }) => {
          if (cancelled) return;
          setHistory(data.rows || data.boxes || data.items || []);
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }

    return () => {
      cancelled = true;
    };
  }, [tab, date, month, search, issueFilter, partnerId]);

  const exportConfig = (() => {
    if (tab === 'inbound') {
      return {
        endpoint: '/logistics/dashboard/warehouse/inbound',
        filename: `warehouse-inbound-${date}.csv`,
        params: {
          date,
          partnerId: partnerId ?? undefined,
          issue: issueFilter || undefined,
        },
      };
    }
    if (tab === 'outbound') {
      return {
        endpoint: '/logistics/dashboard/warehouse/outbound',
        filename: `warehouse-outbound-${date}.csv`,
        params: {
          date,
          partnerId: partnerId ?? undefined,
          issue: issueFilter || undefined,
        },
      };
    }
    if (tab === 'storage') {
      return {
        endpoint: '/logistics/dashboard/warehouse/storage',
        filename: `warehouse-storage-${month}.csv`,
        params: { month, partnerId: partnerId ?? undefined },
      };
    }
    return {
      endpoint: '/logistics/dashboard/warehouse/box-history',
      filename: `warehouse-box-history-${month}.csv`,
      params: {
        month,
        partnerId: partnerId ?? undefined,
        search: search || undefined,
      },
    };
  })();

  return (
    <>
      <div className="admin-tab-bar admin-tab-bar--sub">
        <button
          type="button"
          className={tab === 'inbound' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
          onClick={() => setTab('inbound')}
        >
          Inbound
        </button>
        <button
          type="button"
          className={tab === 'outbound' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
          onClick={() => setTab('outbound')}
        >
          Outbound
        </button>
        <button
          type="button"
          className={tab === 'storage' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
          onClick={() => setTab('storage')}
        >
          Storage
        </button>
        <button
          type="button"
          className={tab === 'box-history' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
          onClick={() => setTab('box-history')}
        >
          Box History
        </button>
      </div>

      <AdminNotice intent="error">{netError}</AdminNotice>

      <section className="admin-warehouse-forecast">
        <FilterBar
          actions={
            <ExportCsvButton
              endpoint={exportConfig.endpoint}
              filename={exportConfig.filename}
              params={exportConfig.params}
              disabled={partnerId == null}
            />
          }
        >
          {(tab === 'inbound' || tab === 'outbound') && (
            <>
              <FilterBar.Item label="Date" htmlFor="warehouse-date">
                <DateDropdown
                  id="warehouse-date"
                  value={date || null}
                  onChange={(iso) => setDate(iso ?? '')}
                  label="Date"
                  placeholder="Select date"
                />
              </FilterBar.Item>
              <FilterBar.Item label="Issue" htmlFor="warehouse-issue">
                <select
                  id="warehouse-issue"
                  value={issueFilter}
                  onChange={(e) => setIssueFilter(e.target.value as OrderIssueType | '')}
                >
                  <option value="">All</option>
                  <option value="damaged">Damaged</option>
                  <option value="overweight">Overweight</option>
                  <option value="repack">Repack</option>
                  <option value="other">Other</option>
                </select>
              </FilterBar.Item>
            </>
          )}
          {(tab === 'storage' || tab === 'box-history') && (
            <FilterBar.Item label="Month" htmlFor="warehouse-month">
              <input
                id="warehouse-month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              />
            </FilterBar.Item>
          )}
          {tab === 'box-history' && (
            <FilterBar.Item label="Search" htmlFor="warehouse-search">
              <input
                id="warehouse-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="BID or order id"
              />
            </FilterBar.Item>
          )}
        </FilterBar>

        {partnerId == null ? (
          <EmptyState
            variant="select-prerequisite"
            title="Select a partner"
            body="Pick a partner from the picker above to load the warehouse dashboard."
          />
        ) : tab === 'inbound' ? (
          <DataTable<InboundOrderRow>
            columns={INBOUND_COLUMNS}
            rows={inbound}
            keyOf={(r, i) => r.orderId ?? i}
            loading={loading}
            emptyMessage="No inbound orders."
          />
        ) : tab === 'outbound' ? (
          <DataTable<OutboundOrderRow>
            columns={OUTBOUND_COLUMNS}
            rows={outbound}
            keyOf={(r, i) => r.orderId ?? i}
            loading={loading}
            emptyMessage="No outbound orders."
          />
        ) : tab === 'storage' ? (
          <DataTable<StorageRow>
            columns={STORAGE_COLUMNS}
            rows={storage}
            keyOf={(r, i) => r.orderId ?? i}
            loading={loading}
            emptyMessage="No storage rows."
          />
        ) : (
          <DataTable<BoxHistoryRow>
            columns={BOX_HISTORY_COLUMNS}
            rows={history_}
            keyOf={(r, i) => r.bid ?? i}
            loading={loading}
            emptyMessage="No box history."
          />
        )}
      </section>
    </>
  );
}
